import { useEffect, useMemo, useSyncExternalStore } from 'react';

import type { JSONDocNode } from '@atlaskit/editor-json-transformer';

import { getRendererInlineCommentOptions } from '@confluence/annotation-provider-inline-comments';
import {
	useAnnotationsDispatch,
	useAnnotationManager,
	useAnnotations,
} from '@confluence/annotation-provider-store';
import { useIsHighlightOptionPanelEnabled } from '@confluence/comments-util/entry-points/useIsHighlightOptionPanelEnabled';
import { expVal } from '@confluence/feature-experiments';

export type RendererAnnotationProviderOptions = {
	pageId: string;
	validAnnotationsCallback: (validAnnotations: string[]) => void;
	triggerAnalyticsEvent: any;
	updateDocument: (adfDoc: JSONDocNode) => void;
	isArchived?: boolean;
};

/**
 * @internal Only exported for testing purposes
 */
export const useCommentNetworkStatus = () => {
	const isOnlineFromNetworkState = useSyncExternalStore(
		(callback) => {
			window.addEventListener('online', callback);
			window.addEventListener('offline', callback);
			return () => {
				window.removeEventListener('online', callback);
				window.removeEventListener('offline', callback);
			};
		},
		() => navigator.onLine,
		() => true,
	);

	const { setIsOffline } = useAnnotationsDispatch();
	const { isOffline: isOfflineFromAnnotationsContext } = useAnnotations();

	useEffect(() => {
		if (isOfflineFromAnnotationsContext === !isOnlineFromNetworkState) {
			// No need to update the state if the value is the same
			return;
		}

		// We only want to activate this feature if the user is part of the offline editing experiment
		if (expVal('platform_editor_offline_editing_web', 'isEnabled', false)) {
			setIsOffline(!isOnlineFromNetworkState);
		}
	}, [isOnlineFromNetworkState, isOfflineFromAnnotationsContext, setIsOffline]);
};

export const useRendererAnnotationProviderOptions = ({
	pageId,
	validAnnotationsCallback,
	triggerAnalyticsEvent,
	updateDocument,
	isArchived,
}: RendererAnnotationProviderOptions) => {
	const { setSelectionProps, setViewProps, setHoverProps } = useAnnotationsDispatch();
	const { isHighlightOptionPanelEnabled } = useIsHighlightOptionPanelEnabled();
	const annotationManager = useAnnotationManager();

	useCommentNetworkStatus();

	return useMemo(
		() => ({
			inlineComment: getRendererInlineCommentOptions({
				pageId,
				validAnnotationsCallback,
				triggerAnalyticsEvent,
				setSelectionProps,
				setHoverProps,
				setViewProps,
				updateDocument,
				isArchived,
				isHighlightOptionPanelEnabled,
			}),
			annotationManager,
		}),
		[
			pageId,
			validAnnotationsCallback,
			triggerAnalyticsEvent,
			setSelectionProps,
			setHoverProps,
			setViewProps,
			updateDocument,
			isArchived,
			isHighlightOptionPanelEnabled,
			annotationManager,
		],
	);
};
